import React from 'react'
import styled from 'styled-components'
import {connect} from 'react-redux'
import {injectIntl} from 'react-intl'
import {Delete, FileCopy, Visibility, VisibilityOff} from '@mui/icons-material'
import Tooltip from '@mui/material/Tooltip'
import DesktopHeader from 'containers/Header/Desktop'
import MobileHeader from 'containers/Header/Mobile'
import Sidebar from 'containers/Sidebar'
import Navigation from 'components/Navigation'
import Container from 'components/Container'
import RecordGrid from 'components/RecordGrid'
import RecordBottom from 'components/RecordBottom'
import TableTop from 'containers/TableTop'
import RecordSideButtons from 'containers/RecordSideButtons'
import {fetchRenameCol, fetchAddCol, resetCreatedCol} from 'modules/entities/actions'
import {
    initRecord,
    updateRecord,
    duplicateRecord,
    deleteRecord,
    resetRecord
} from 'modules/record/actions'
import {fetchRelations, createRelation, updateRelation} from 'modules/relations/actions'
import {isMobile, getEntityFields, getView, getValueByPath} from 'utils/utils'
import {fieldtype} from 'utils/enums/fieldtype'
import route from 'utils/enums/route'
import ColumnIcon from './ColumnIcon'

const Actions = styled.div`
    display: flex;
    margin: auto 16px 10px 0;
    align-items: center;
`

const IconButton = styled.button`
    background-color: transparent;
    color: #a0b7be;
    font-size: 10px;
    width: 60px;
    height: 45px;
    padding: 0;
    border: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    overflow: hidden;
    white-space: nowrap;

    svg {
        font-size: 28px;
    }

    :focus {
        outline: none;
    }
`

const ButtonAdd = styled.div`
    font-size: 16px;
    color: #415dd4;
    align-items: center;
    cursor: pointer;
    display: flex;

    > svg {
        margin-right: 8px;
        min-width: 32px;
        min-height: 32px;
    }
`

class Record extends React.Component {
    constructor(props) {
        super(props)

        this.state = {
            targetCol: null,
            showHidden: false
        }
    }

    componentDidMount() {
        this.props.initRecord(Number(this.props.params.table), Number(this.props.params.record))
        this.props.fetchRelations(Number(this.props.params.table), route.Record)

        this.view = this.props.params.view ? getView(Number(this.props.params.view)) : null
    }

    componentDidUpdate(props) {
        if (this.props.initError) {
            this.props.navigate('/')
        }

        if (this.props.duplication) {
            const {view, table} = this.props.params
            this.props.navigate(`/${route.Record}/${table}/${this.props.duplication}${view ? '/' + view : ''}`)
			this.props.resetRecord()
        }

        if (this.props.deletion) {
            this.props.navigate(`/${route.Table}/${this.props.params.table}`)
			this.props.resetRecord()
        }

        if (props.params.record !== this.props.params.record ||
            props.params.table !== this.props.params.table) {
            this.props.initRecord(Number(this.props.params.table), Number(this.props.params.record))
            this.props.fetchRelations(Number(this.props.params.table), route.Record)
            this.setState({ targetCol: null })
        }

        if (this.props.params.view !== props.params.view) {
            this.view = this.props.params.view ? getView(Number(this.props.params.view)) : null
        }

        if (this.props.colId && this.props.colId !== props.colId) {
            const el = document.getElementById('wrapper')
            el.scrollTo(0, el.scrollHeight)
            document.querySelector(`[data-cell='${this.props.colId}']`).select()
            this.props.resetCreatedCol()
        }
    }

    handleUpdateValue = (value, name) => {
        this.props.updateRecord(this.props.recordId, name, value)
    }

    deleteRecord = () => {
        this.props.deleteRecord([this.props.recordId])
    }

    handleSelectCol = (targetCol) => {
        this.setState({ targetCol })
    }

    handleCloseMenu = () => {
        this.setState({ targetCol: null })
    }

    createCol = () => {
        this.props.fetchAddCol(this.props.entityId)
    }

    getOrder = (columns, order) => {
        const result = order ? [...order] : []
        const data = [...columns]

        columns.forEach((column) => {
            if (result.indexOf(column.name) === -1) {
                result.push(column.name)
            }
        })

        if (order) {
            order.forEach((item) => {
                if (columns.findIndex(column => column.name === item) === -1) {
                    result.splice(result.indexOf(item), 1)
                }
            })
        }

        return data.sort((a, b) => {
            let indexA = result.indexOf(a.name)
            let indexB = result.indexOf(b.name)
            if (indexA === -1) indexA = result.length
            if (indexB === -1) indexB = result.length
            return indexA - indexB
        })
    }

    toggleVisibility = () => {
        this.setState(state => ({showHidden: !state.showHidden}))
    }

    render() {
        const {intl, entities, entityId} = this.props
        const mobile = isMobile()
        const table = entities.find(item => item.id === entityId)
        const connection = table ? !!table.connection : false
        const fields = getEntityFields(entityId)
        const fieldsVisible = this.view && !this.view.isGlobal ? fields && fields.filter(field =>
            !this.view.state.fieldsHidden.find(obj => obj === field.id) || this.state.showHidden) : fields
        const data = fieldsVisible && fieldsVisible.filter(field => field.uiType !== fieldtype.pointer).map(item => {
                const defaultValue = item.uiType === fieldtype.linkedfield || item.uiType === fieldtype.lookup ||
                    item.uiType === fieldtype.formula ? this.props.record['d' + item.id] : this.props.record['c' + item.id]

                const lookupField = item.uiType === fieldtype.lookup && item.foreignerFCNs.length === 1 ?
					item.foreignerFields[0] : null

				const min = item.uiType === fieldtype.datetime && item.uiConfig.mindate ? getValueByPath(this.props.record, item.uiConfig.mindate) : null

				const max = item.uiType === fieldtype.datetime && item.uiConfig.maxdate ? getValueByPath(this.props.record, item.uiConfig.maxdate) : null

                return {
                    name: item.id,
                    title: item.singular,
                    defaultValue,
                    type: item.uiType,
                    config: item.uiConfig,
                    foreignerEcn: item.foreignerEcn,
                    linkedRelation: this.props.record['c' + item.linkedRelation[0]],
                    lookupConfig: lookupField && lookupField.uiConfig,
                    lookupType: lookupField && lookupField.uiType,
					min,
					max,
                    connection: !!item.connection
                }
            })

        const cells = this.view && !this.view.isGlobal ? this.getOrder(data, this.view.state.fieldsOrder) : data
        const targetCol = this.state.targetCol ? fields.find(item => item.id === this.state.targetCol) : null

        return (
            <React.Fragment>
                {mobile ?
                    <MobileHeader
                        logo={this.props.logo}
                    >
                        <span>{this.props.recordTitle}</span>
                    </MobileHeader>
                    :
                    <DesktopHeader padding={global.SIDE_BAR_SMALL_WIDTH}>
                        {this.props.current &&
                            <Navigation
                                application={this.props.applications.filter(item => item.id === this.props.current)[0].name}
                                table={table && table.singular}
                                tableId={entityId}
                                permissions={this.props.permissions}
                                recordId={this.props.recordId}
                                record={this.props.recordTitle}
                            />
                        }
                        {this.state.targetCol ?
                            <TableTop
                                type={targetCol.uiType}
                                config={targetCol.uiConfig}
                                fieldName={targetCol.title}
                                handleCloseMenu={this.handleCloseMenu}
                                entitiesCount={entities.length}
                                entity={entityId}
                                targetCol={this.state.targetCol}
                                linkedTemplate={targetCol.linkedTemplate}
                                foreignerEcn={targetCol.foreignerEcn}
                                relation={targetCol.linkedRelation}
                                foreignerFcns={targetCol.foreignerFCNs}
                                documentPage={true}
                                connection={targetCol.connection}
                            />
                            :
                            <Actions>
                                {this.props.permissions.Write && !connection &&
                                    <IconButton onClick={this.props.duplicateRecord}>
                                        <FileCopy />
                                        {intl.formatMessage({id: "app.duplicate"})}
                                    </IconButton>
                                }
                                {this.props.permissions.Delete && !connection &&
                                    <IconButton onClick={this.deleteRecord}>
                                        <Delete />
                                        {intl.formatMessage({id: "record.delete"})}
                                    </IconButton>
                                }
                                <div style={{marginLeft: 'auto', display: 'flex', alignItems: 'center'}}>
                                    {this.view && !this.view.isGlobal && !!this.view.state.fieldsHidden.length &&
                                        <React.Fragment>
                                            {this.state.showHidden ?
                                                <Tooltip
                                                    title={intl.formatMessage({id: "table.no_hidden_fields"})}
                                                    placement="top"
                                                >
                                                    <VisibilityOff onClick={this.toggleVisibility} sx={{color: '#415dd4', marginRight: '20px', cursor: 'pointer'}} />
                                                </Tooltip> :
                                                <Tooltip
                                                    title={intl.formatMessage({id: "table.hidden_fields"})}
                                                    placement="top"
                                                >
                                                    <Visibility onClick={this.toggleVisibility} sx={{marginRight: '20px', cursor: 'pointer'}} />
                                                </Tooltip>
                                            }
                                        </React.Fragment>
                                    }
                                    {this.props.permissions.Designer &&
                                        <ButtonAdd onClick={this.createCol}>
                                            <ColumnIcon />
                                            {intl.formatMessage({id: "table.add_field"})}
                                        </ButtonAdd>
                                    }
                                </div>
                            </Actions>
                        }
                    </DesktopHeader>
                }
                {!mobile &&
                    <Sidebar size={global.SIDE_BAR_SMALL_WIDTH} logo={this.props.logo}>
                        <RecordSideButtons
                            entity={entityId}
                            name={table && table.singular}
                        />
                    </Sidebar>
                }
                <Container
                    mobile={mobile}
                    bottom
                    marginTop={mobile ? 60 : 117}
                    style={{padding: '20px 20px 0 20px'}}
                >
                    <RecordGrid
                        data={cells}
                        handleUpdateValue={this.handleUpdateValue}
                        permissions={this.props.permissions}
                        mobile={mobile}
                        fetchRenameCol={this.props.fetchRenameCol}
                        handleFocus={this.handleSelectCol}
                    />
                </Container>
                <RecordBottom
                    mobile={mobile}
                    relations={this.props.relations}
                    recordId={this.props.recordId}
                    entities={entities}
                    entityId={entityId}
                    createRelation={this.props.createRelation}
                    updateRelation={this.props.updateRelation}
                    permissions={this.props.permissions}
                />
            </React.Fragment>
        )
    }
}

const mapStateToProps = ({entities, record, applications, login, relations}) => ({
    entities: entities.entities,
    record: record.record,
    recordId: record.recordId,
    recordTitle: record.recordTitle,
    entityId: record.entityId,
    initError: record.initError,
    applications: applications.data,
    logo: applications.logo,
    current: applications.current,
    relations: relations.data,
    permissions: login.permissions,
    duplication: record.duplicated,
    deletion: record.deleted,
    colId: entities.createdCol
})

export default connect(mapStateToProps, {
    initRecord,
    updateRecord,
    duplicateRecord,
    deleteRecord,
    fetchRelations,
    resetRecord,
    createRelation,
    updateRelation,
    fetchRenameCol,
    fetchAddCol,
    resetCreatedCol
})(injectIntl(Record))
